import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import { createUuid } from "../../../lib/trace/context";
import type { components } from "../../../api/generated";
import { CONTACT_IMPORT_API_PATH } from "../config";
import type {
  ContactImportAction,
  ContactImportExecutionResult,
  ContactImportExecutionRow,
  ContactImportNormalizedRow,
  ContactImportRow,
  ContactImportRowsPayload,
  ContactImportTask,
} from "../types";

interface RawImportRow {
  id: number;
  source_sheet: string;
  source_row_no: number;
  normalized_json: ContactImportNormalizedRow;
  match_result_json: ContactImportRow["matchResult"];
  action_type: ContactImportAction | "update" | "record_only";
  update_policy_json?: Record<string, unknown>;
  validation_status: ContactImportRow["validationStatus"];
  issues_json?: ContactImportRow["issues"];
  execution_status: string;
  execution_result_json?: RawExecutionRow | null;
}

interface RawExecutionRow {
  row_id: number;
  source_sheet?: string | null;
  source_row_no?: number | null;
  status: ContactImportExecutionRow["status"];
  action_type?: ContactImportAction | "update" | "record_only";
  is_duplicate: boolean;
  reason_code?: string | null;
  message: string;
  contact_id?: number | null;
  record_id?: number | null;
  org_unit_id?: number | null;
}

interface RawExecutionResult {
  status: string;
  total: number;
  succeeded: number;
  failed: number;
  created: number;
  updated: number;
  recorded: number;
  duplicate: number;
  blocked: number;
  skipped: number;
  rows: RawExecutionRow[];
}

interface RawImportTask {
  id: number;
  task_no: string;
  status: string;
  version: number;
  source_type: "file" | "paste";
  file_name?: string | null;
  preview_digest?: string | null;
  validation_summary_json?: Record<string, number>;
  rows?: RawImportRow[];
}

type GeneratedTaskCreate = components["schemas"]["ContactImportTaskCreate"];
type GeneratedPastePayload = components["schemas"]["ContactImportPastePayload"];
type GeneratedExecutePayload = components["schemas"]["ContactImportExecutePayload"];

/** 创建联系人导入任务。 */
export async function createContactImportTask(
  sourceType: "file" | "paste",
): Promise<ContactImportTask> {
  const response = await httpClient.post<ApiEnvelope<RawImportTask>>(CONTACT_IMPORT_API_PATH, {
    source_type: sourceType,
  } satisfies GeneratedTaskCreate);
  return mapTask(requireData(response.data));
}

/** 上传文件并生成预览，解析请求禁止自动重试。 */
export async function parseContactImportFile(
  taskId: number,
  version: number,
  file: File,
): Promise<ContactImportTask> {
  const form = new FormData();
  form.append("version", String(version));
  form.append("file", file);
  const response = await httpClient.post<ApiEnvelope<RawImportTask>>(
    `${CONTACT_IMPORT_API_PATH}/${taskId}/parse-file`,
    form,
    { timeout: 600_000, "axios-retry": { retries: 0 } },
  );
  return mapTask(requireData(response.data));
}

/** 粘贴 Excel HTML/TSV 并生成预览，解析请求禁止自动重试。 */
export async function parseContactImportPaste(
  taskId: number,
  version: number,
  html: string | null,
  text: string,
): Promise<ContactImportTask> {
  const response = await httpClient.post<ApiEnvelope<RawImportTask>>(
    `${CONTACT_IMPORT_API_PATH}/${taskId}/parse-paste`,
    { version, html, text } satisfies GeneratedPastePayload,
    { timeout: 600_000, "axios-retry": { retries: 0 } },
  );
  return mapTask(requireData(response.data));
}

/** 保存用户编辑后的标准化行。 */
export async function saveContactImportRows(
  taskId: number,
  payload: ContactImportRowsPayload,
): Promise<ContactImportTask> {
  const response = await httpClient.put<ApiEnvelope<RawImportTask>>(
    `${CONTACT_IMPORT_API_PATH}/${taskId}/rows`,
    payload,
  );
  return mapTask(requireData(response.data));
}

/** 执行已确认的有效行，执行请求禁止自动重试。 */
export async function executeContactImport(
  task: ContactImportTask,
  rowIds: number[],
): Promise<ContactImportExecutionResult> {
  if (!task.previewDigest) throw new Error("预览尚未固化，请先保存并重新校验");
  const response = await httpClient.post<ApiEnvelope<RawExecutionResult>>(
    `${CONTACT_IMPORT_API_PATH}/${task.id}/execute`,
    {
      version: task.version,
      preview_digest: task.previewDigest,
      row_ids: rowIds,
    } satisfies GeneratedExecutePayload,
    {
      headers: { "Idempotency-Key": createIdempotencyKey(task.id) },
      timeout: 120_000,
      "axios-retry": { retries: 0 },
    },
  );
  return mapExecutionResult(requireData(response.data));
}

/** 下载联系人标准模板。 */
export async function downloadContactImportTemplate(): Promise<Blob> {
  const response = await httpClient.get<Blob>(`${CONTACT_IMPORT_API_PATH}/template`, {
    responseType: "blob",
  });
  return response.data;
}

/** 下载按原名单顺序整理的未导入 Excel。 */
export async function downloadContactImportUnimported(taskId: number): Promise<Blob> {
  const response = await httpClient.get<Blob>(`${CONTACT_IMPORT_API_PATH}/${taskId}/unimported`, {
    responseType: "blob",
  });
  return response.data;
}

/** 把后端蛇形任务契约映射为前端领域对象。 */
function mapTask(raw: RawImportTask): ContactImportTask {
  return {
    id: raw.id,
    taskNo: raw.task_no,
    status: raw.status,
    version: raw.version,
    sourceType: raw.source_type,
    fileName: raw.file_name,
    previewDigest: raw.preview_digest,
    validationSummary: raw.validation_summary_json ?? {},
    rows: (raw.rows ?? []).map((row) => {
      const mappedRow = {
        id: row.id,
        sourceSheet: row.source_sheet,
        sourceRowNo: row.source_row_no,
        normalized: row.normalized_json,
        matchResult: row.match_result_json,
        actionType: row.action_type === "create" || row.action_type === "update"
          ? row.action_type : "skip",
        updatePolicy: row.update_policy_json ?? {},
        validationStatus: row.validation_status,
        issues: row.issues_json ?? [],
        selected: false,
        executionStatus: row.execution_status,
        executionResult: row.execution_result_json
          ? mapExecutionRow(row.execution_result_json)
          : undefined,
      };
      return { ...mappedRow, selected: isRawImportRowExecutable(row) };
    }),
  };
}

/** 判断后端原始行是否默认勾选为本次可导入行。 */
function isRawImportRowExecutable(row: RawImportRow): boolean {
  return (
    !row.match_result_json.execution_blocked &&
    ((row.action_type === "create" && row.match_result_json.system_exists === "no") ||
      (row.action_type === "update" && row.match_result_json.system_exists === "yes" &&
        row.match_result_json.allowed_actions?.includes("update") === true)) &&
    row.validation_status !== "blocked" &&
    row.execution_status !== "succeeded" &&
    !["created", "updated", "recorded"].includes(row.execution_result_json?.status ?? "")
  );
}

/** 把执行结果映射为前端领域对象。 */
function mapExecutionResult(raw: RawExecutionResult): ContactImportExecutionResult {
  return {
    status: raw.status,
    total: raw.total,
    succeeded: raw.succeeded,
    failed: raw.failed,
    created: raw.created,
    updated: raw.updated,
    recorded: raw.recorded,
    duplicate: raw.duplicate,
    blocked: raw.blocked,
    skipped: raw.skipped,
    rows: raw.rows.map(mapExecutionRow),
  };
}

/** 把单行执行结果映射为前端领域对象。 */
function mapExecutionRow(raw: RawExecutionRow): ContactImportExecutionRow {
  return {
    rowId: raw.row_id,
    sourceSheet: raw.source_sheet,
    sourceRowNo: raw.source_row_no,
    status: raw.status,
    actionType: raw.action_type,
    isDuplicate: raw.is_duplicate,
    reasonCode: raw.reason_code,
    message: raw.message,
    contactId: raw.contact_id,
    recordId: raw.record_id,
    orgUnitId: raw.org_unit_id,
  };
}

/** 读取统一响应数据。 */
function requireData<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.data === undefined || envelope.data === null) {
    throw new Error("联系人导入接口未返回数据");
  }
  return envelope.data;
}

/** 为单次用户执行动作生成幂等键。 */
function createIdempotencyKey(taskId: number): string {
  return `contact-import-${taskId}-${Date.now()}-${createUuid()}`;
}
